import React from 'react';
import { Rocket, AlertTriangle } from 'lucide-react';
import { ConfirmModal } from './ConfirmModal';

interface PublishConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPublished: boolean;
  siteName: string;
  isLoading?: boolean;
  /** When false, omits Roary mascot/branding on the go-live confirm dialog. */
  showRoaryBranding?: boolean;
}

export const PublishConfirmModal: React.FC<PublishConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isPublished,
  siteName,
  isLoading = false,
  showRoaryBranding = true,
}) => {
  const actionTitle = isPublished ? 'Take Website Offline' : 'Go Live';
  const message = isPublished
    ? `You are about to take "${siteName}" offline. This will make your website inaccessible to visitors. It will no longer be available at your subdomain.`
    : `You are about to make "${siteName}" live. This will make your website accessible to visitors at your subdomain. Anyone with the link will be able to view it.`;

  const confirmText = isPublished ? 'Take Offline' : 'Go Live';
  const loadingText = isPublished ? 'Taking offline...' : 'Going live...';

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      type={isPublished ? 'danger' : 'success'}
      title={actionTitle}
      message={message}
      confirmText={confirmText}
      cancelText="Cancel"
      isLoading={isLoading}
      loadingText={loadingText}
      showRoary={showRoaryBranding && !isPublished}
    >
      {!isPublished && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Rocket className="w-3 h-3 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">Ready to go live!</p>
              <p className="text-xs text-blue-700">
                Make sure your website has at least one page set as the home page before publishing.
              </p>
            </div>
          </div>
        </div>
      )}

      {isPublished && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900 mb-1">
                Website will be taken offline
              </p>
              <p className="text-xs text-amber-700">
                Visitors will see a &quot;Website not found&quot; message when trying to access your
                site.
              </p>
            </div>
          </div>
        </div>
      )}
    </ConfirmModal>
  );
};
